import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Monitor, Smartphone, MapPin, LogOut, ShieldOff, RefreshCw, Settings as SettingsIcon } from 'lucide-react';

/** /security — combines:
 *   • Active Sessions (any authenticated user)
 *   • Security Settings sliders (admin only)
 *
 *  This is the "Device Sessions" + "Idle Timeout config" page the user asked for.
 */
const SecuritySessions = ({ user }) => {
  // user prop may be null on first render — fall back to localStorage so the
  // admin-only panel reliably appears.
  const fallbackUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; }
  })();
  const effectiveUser = user || fallbackUser;
  const isAdmin = (effectiveUser?.role || '').toLowerCase() === 'admin';
  const [sessions, setSessions] = useState([]);
  const [currentSid, setCurrentSid] = useState(null);
  const [loading, setLoading] = useState(true);

  // settings (admin)
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/sessions');
      setSessions(data.sessions || []);
      setCurrentSid(data.current_session_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axiosInstance.get('/security/settings');
      setSettings(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load security settings');
    }
  };

  useEffect(() => { fetchSessions(); fetchSettings(); /* eslint-disable-next-line */ }, []);

  const revoke = async (id) => {
    if (!window.confirm('Sign this device out?')) return;
    try {
      await axiosInstance.delete(`/sessions/${id}`);
      toast.success('Device signed out');
      fetchSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    }
  };

  const revokeAllOthers = async () => {
    if (!window.confirm('Sign out from all OTHER devices? This device will stay signed in.')) return;
    try {
      const { data } = await axiosInstance.post('/sessions/revoke-all-others');
      toast.success(`${data.revoked} device(s) signed out`);
      fetchSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await axiosInstance.put('/security/settings', {
        idle_timeout_minutes: settings.idle_timeout_minutes,
        idle_warning_seconds: settings.idle_warning_seconds,
        access_token_minutes: settings.access_token_minutes,
        refresh_token_days: settings.refresh_token_days,
        max_failed_attempts: settings.max_failed_attempts,
        lock_minutes: settings.lock_minutes,
      });
      setSettings(data);
      // sync new idle timing to localStorage so the current tab honours it immediately
      localStorage.setItem('idle_timeout_minutes', String(data.idle_timeout_minutes));
      localStorage.setItem('idle_warning_seconds', String(data.idle_warning_seconds));
      toast.success('Security settings updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const iconFor = (label = '') => {
    const s = label.toLowerCase();
    if (s.includes('mobile') || s.includes('android') || s.includes('iphone') || s.includes('ipad') || s.includes('ios')) {
      return <Smartphone className="w-5 h-5 text-indigo-600" />;
    }
    return <Monitor className="w-5 h-5 text-slate-600" />;
  };

  return (
    <div data-testid="security-sessions-page" className="space-y-6">
      <div className="page-header">
        <h2 className="page-title">Security &amp; Sessions</h2>
        <div className="flex gap-2">
          <button onClick={fetchSessions} className="btn-secondary inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {sessions.length > 1 && (
            <button
              data-testid="revoke-all-others"
              onClick={revokeAllOthers}
              className="btn-primary inline-flex items-center gap-2 bg-red-600 hover:bg-red-700"
            >
              <ShieldOff className="w-4 h-4" /> Sign out all other devices
            </button>
          )}
        </div>
      </div>

      {/* Active Sessions */}
      <section className="bg-white rounded-xl border p-5">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Monitor className="w-5 h-5 text-indigo-600" /> Active Devices
        </h3>
        {loading ? (
          <p className="text-sm text-slate-500">Loading...</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-slate-500">No active sessions.</p>
        ) : (
          <ul className="divide-y">
            {sessions.map(s => (
              <li key={s.id} data-testid={`session-row-${s.id}`} className="py-3 flex items-center gap-4">
                {iconFor(s.device_label)}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 flex items-center gap-2">
                    {s.device_label || 'Unknown device'}
                    {s.is_current && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-semibold">
                        This device
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                    {s.ip && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.ip}</span>}
                    <span>last active: {new Date(s.last_active).toLocaleString()}</span>
                    <span>expires: {new Date(s.expires_at).toLocaleDateString()}</span>
                  </p>
                </div>
                {!s.is_current && (
                  <button
                    data-testid={`revoke-session-${s.id}`}
                    onClick={() => revoke(s.id)}
                    className="px-3 py-1.5 rounded-lg text-sm border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Settings (admin only) */}
      {isAdmin && settings && (
        <section className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-indigo-600" /> Session &amp; Lockout Policy
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SettingField label="Idle Timeout (minutes)" testId="idle-timeout"
              value={settings.idle_timeout_minutes}
              onChange={(v) => setSettings({ ...settings, idle_timeout_minutes: v })} min={1} max={1440} />
            <SettingField label="Idle Warning (seconds)" testId="idle-warning"
              value={settings.idle_warning_seconds}
              onChange={(v) => setSettings({ ...settings, idle_warning_seconds: v })} min={5} max={600} />
            <SettingField label="Access Token (minutes)" testId="access-token-min"
              value={settings.access_token_minutes}
              onChange={(v) => setSettings({ ...settings, access_token_minutes: v })} min={1} max={1440} />
            <SettingField label="Refresh Token (days)" testId="refresh-days"
              value={settings.refresh_token_days}
              onChange={(v) => setSettings({ ...settings, refresh_token_days: v })} min={1} max={365} />
            <SettingField label="Max Failed Attempts" testId="max-failed"
              value={settings.max_failed_attempts}
              onChange={(v) => setSettings({ ...settings, max_failed_attempts: v })} min={1} max={50} />
            <SettingField label="Lock Duration (minutes)" testId="lock-minutes"
              value={settings.lock_minutes}
              onChange={(v) => setSettings({ ...settings, lock_minutes: v })} min={1} max={1440} />
          </div>
          <div className="mt-5 flex justify-end">
            <button
              data-testid="save-security-settings"
              onClick={saveSettings}
              disabled={savingSettings}
              className="btn-primary"
            >
              {savingSettings ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

const SettingField = ({ label, value, onChange, min, max, testId }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <input
      data-testid={testId}
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value || min, 10))}
      className="form-input"
    />
  </div>
);

export default SecuritySessions;
